import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Minus, Plus, Check, ArrowDownToLine, ArrowUpFromLine, Truck, PackageCheck, PackageX, Clock, X } from 'lucide-react';
import { PRODUCTOS } from '../constants/productos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP = (allowHalf) => allowHalf ? 0.5 : 1;
const parseQty = (val, allowHalf) => {
  if (allowHalf) {
    const f = parseFloat(val) || 0;
    return Math.round(f * 2) / 2;
  }
  return parseInt(val) || 0;
};
const fmtQty = (qty) => qty % 1 !== 0 ? qty.toFixed(1) : String(qty);

export function Movimiento() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialTipo = location.state?.tipo || 'salida';

  const [tipo, setTipo] = useState(initialTipo);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Salida state
  const [puntosVenta, setPuntosVenta] = useState([]);
  const [destino, setDestino] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [inventario, setInventario] = useState({});
  const [enTransito, setEnTransito] = useState({});
  const [historialSalidas, setHistorialSalidas] = useState([]);

  // Entrada state
  const [transitos, setTransitos] = useState([]);
  const [transitoSeleccionado, setTransitoSeleccionado] = useState(null);
  const [cantConfirmadas, setCantConfirmadas] = useState({});
  const [historialEntradas, setHistorialEntradas] = useState([]);
  const [cancelando, setCancelando] = useState(null); // id del tránsito a cancelar
  const [rechazando, setRechazando] = useState(false); // muestra el formulario de rechazo
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [rechazado, setRechazado] = useState(false);

  useEffect(() => {
    loadData();
  }, [tipo]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: diaCerrado } = await axios.get(`${API}/dia-cerrado/${user.punto_venta_id}`);
      if (diaCerrado.cerrado) {
        setError('El día ya fue cerrado. No se pueden registrar movimientos.');
        setLoading(false);
        return;
      }

      if (tipo === 'salida') {
        const [invRes, puntosRes, histRes] = await Promise.all([
          axios.get(`${API}/inventario/actual/${user.punto_venta_id}`),
          axios.get(`${API}/puntos-venta/estados`),
          axios.get(`${API}/transitos/historial-salidas/${user.punto_venta_id}`),
        ]);
        setInventario(invRes.data.productos || {});
        setEnTransito(invRes.data.en_transito || {});
        const allPuntos = puntosRes.data;
        const miPunto = allPuntos.find(p => p.id === user.punto_venta_id);
        const esMoto = miPunto?.categoria === 'moto';
        setPuntosVenta(allPuntos.filter(p => {
          if (p.id === user.punto_venta_id) return false;
          if (esMoto) return true;
          return p.categoria === 'moto';
        }));
        setHistorialSalidas(histRes.data);
      } else {
        const [transitosRes, histRes] = await Promise.all([
          axios.get(`${API}/transitos/pendientes/${user.punto_venta_id}`),
          axios.get(`${API}/transitos/historial-entradas/${user.punto_venta_id}`),
        ]);
        setTransitos(transitosRes.data);
        setHistorialEntradas(histRes.data);
      }
    } catch (e) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const disponible = (clave) => Math.max(0, (inventario[clave] || 0) - (enTransito[clave] || 0));

  const handleIncrement = (clave, allowHalf) => {
    const current = quantities[clave] || 0;
    const step = STEP(allowHalf);
    if (current + step <= disponible(clave))
      setQuantities(prev => ({ ...prev, [clave]: Math.round((current + step) * 2) / 2 }));
  };

  const handleDecrement = (clave, allowHalf) => {
    const current = quantities[clave] || 0;
    const step = STEP(allowHalf);
    if (current > 0)
      setQuantities(prev => ({ ...prev, [clave]: Math.max(0, Math.round((current - step) * 2) / 2) }));
  };

  const handleIncrementConf = (clave, allowHalf) => {
    const max = transitoSeleccionado?.productos?.[clave] || 0;
    const current = cantConfirmadas[clave] || 0;
    const step = STEP(allowHalf);
    if (current + step <= max)
      setCantConfirmadas(prev => ({ ...prev, [clave]: Math.round((current + step) * 2) / 2 }));
  };

  const handleDecrementConf = (clave, allowHalf) => {
    const current = cantConfirmadas[clave] || 0;
    const step = STEP(allowHalf);
    if (current > 0)
      setCantConfirmadas(prev => ({ ...prev, [clave]: Math.max(0, Math.round((current - step) * 2) / 2) }));
  };

  const selectTransito = (t) => {
    setTransitoSeleccionado(t);
    setCantConfirmadas({ ...t.productos });
  };

  const totalPiezas = () => Object.values(quantities).reduce((s, v) => s + (v || 0), 0);
  const totalConf = () => Object.values(cantConfirmadas).reduce((s, v) => s + (v || 0), 0);

  const handleCancelarTransito = async (id) => {
    try {
      await axios.delete(`${API}/transitos/${id}`);
      setCancelando(null);
      loadData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cancelar tránsito');
      setCancelando(null);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleSubmitSalida = async () => {
    if (!destino) { setError('Selecciona un punto de venta destino'); return; }
    if (totalPiezas() === 0) { setError('Selecciona al menos un producto'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/transitos`, {
        origen_id: user.punto_venta_id,
        destino_id: destino,
        productos: quantities,
      });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al registrar salida');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmarEntrada = async () => {
    if (totalConf() === 0) { setError('Confirma al menos una cantidad'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.put(`${API}/transitos/${transitoSeleccionado.id}/confirmar`, {
        productos_confirmados: cantConfirmadas,
      });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al confirmar entrada');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRechazarEntrada = async () => {
    if (motivoRechazo.trim().length < 3) { setError('Escribe el motivo del rechazo'); return; }
    setError('');
    setSubmitting(true);
    try {
      await axios.put(`${API}/transitos/${transitoSeleccionado.id}/rechazar`, { motivo: motivoRechazo.trim() });
      setRechazado(true);
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al rechazar la entrada');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTipoChange = (t) => {
    setTipo(t);
    setQuantities({});
    setDestino(null);
    setTransitoSeleccionado(null);
    setCantConfirmadas({});
    setSuccess(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="inline-block w-8 h-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (success && rechazado) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 gap-4">
        <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-100">
          <PackageX className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-900">Entrada rechazada</h1>
        <p className="text-zinc-500 text-center">
          No entra a tu inventario. Quedará registrada en la bitácora y en tu corte.
        </p>
        <button onClick={() => navigate(location.state?.volverA || '/pos')} className="mt-4 px-8 h-12 bg-zinc-900 text-white rounded-xl font-medium">
          {location.state?.volverA === '/corte' ? 'Volver al corte' : 'Volver al POS'}
        </button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 gap-4">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center ${tipo === 'entrada' ? 'bg-blue-100' : 'bg-orange-100'}`}>
          {tipo === 'entrada'
            ? <PackageCheck className="w-10 h-10 text-blue-600" />
            : <Truck className="w-10 h-10 text-orange-600" />}
        </div>
        <h1 className="text-2xl font-bold text-zinc-900">
          {tipo === 'entrada' ? 'Entrada confirmada' : 'Salida registrada'}
        </h1>
        <p className="text-zinc-500 text-center">
          {tipo === 'salida'
            ? `${fmtQty(totalPiezas())} piezas en tránsito hacia ${puntosVenta.find(p => p.id === destino)?.name}`
            : `${fmtQty(totalConf())} piezas recibidas de ${transitoSeleccionado?.origen_nombre}`}
        </p>
        <button onClick={() => navigate(location.state?.volverA || '/pos')} className="mt-4 px-8 h-12 bg-zinc-900 text-white rounded-xl font-medium">
          {location.state?.volverA === '/corte' ? 'Volver al corte' : 'Volver al POS'}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="movimiento-page">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
        <button onClick={() => navigate('/pos')} className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-zinc-900">
          {tipo === 'entrada' ? 'Confirmar Entrada' : 'Registrar Salida'}
        </h1>
      </div>

      {/* Toggle */}
      <div className="px-4 py-3 border-b border-zinc-100">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleTipoChange('salida')}
            className={`h-12 rounded-xl font-medium text-base flex items-center justify-center gap-2 transition-all ${tipo === 'salida' ? 'bg-orange-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="btn-tipo-salida"
          >
            <ArrowUpFromLine className="w-5 h-5" />
            Salida
          </button>
          <button
            onClick={() => handleTipoChange('entrada')}
            className={`h-12 rounded-xl font-medium text-base flex items-center justify-center gap-2 transition-all ${tipo === 'entrada' ? 'bg-blue-600 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="btn-tipo-entrada"
          >
            <ArrowDownToLine className="w-5 h-5" />
            Entrada
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ===== SALIDA ===== */}
      {tipo === 'salida' && (
        <>
          {/* Destino selector */}
          <div className="px-4 pt-4 pb-2">
            <p className="text-xs text-zinc-500 font-medium mb-2 uppercase tracking-wide">Punto de venta destino</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {puntosVenta.map(p => (
                <button
                  key={p.id}
                  onClick={() => !p.cerrado && setDestino(p.id)}
                  disabled={p.cerrado}
                  title={p.cerrado ? 'Turno cerrado' : undefined}
                  className={`flex-shrink-0 px-4 h-10 rounded-xl border text-sm font-medium transition-all
                    ${p.cerrado
                      ? 'border-zinc-200 bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      : destino === p.id
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-300 text-zinc-700'}`}
                >
                  {p.name}{p.cerrado ? ' (cerrado)' : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Products */}
          <div className="flex-1 overflow-auto px-4 py-2">
            <div className="space-y-1">
              {PRODUCTOS.map(producto => {
                const qty = quantities[producto.clave] || 0;
                const avail = disponible(producto.clave);
                const transit = enTransito[producto.clave] || 0;
                return (
                  <div key={producto.clave} className="flex items-center justify-between py-3 border-b border-zinc-100" data-testid={`producto-${producto.clave}`}>
                    <div>
                      <p className="text-base font-medium text-zinc-900">{producto.nombre}</p>
                      <div className="flex gap-3 text-xs text-zinc-500">
                        <span>Disponible: {avail}</span>
                        {transit > 0 && <span className="text-amber-600 flex items-center gap-1"><Truck className="w-3 h-3" />{transit} en tránsito</span>}
                        {producto.allowHalf && <span className="text-amber-600 font-medium">· ½</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDecrement(producto.clave, producto.allowHalf)} disabled={qty === 0} className="w-11 h-11 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30">
                        <Minus className="w-5 h-5" />
                      </button>
                      <input
                        type="number"
                        inputMode={producto.allowHalf ? 'decimal' : 'numeric'}
                        min="0"
                        max={avail}
                        step={producto.allowHalf ? '0.5' : '1'}
                        value={qty}
                        onChange={e => {
                          const v = Math.min(avail, Math.max(0, parseQty(e.target.value, producto.allowHalf)));
                          setQuantities(prev => ({ ...prev, [producto.clave]: v }));
                        }}
                        className={`w-16 text-center text-2xl font-bold bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${qty > 0 ? 'text-orange-600' : 'text-zinc-300'}`}
                      />
                      <button onClick={() => handleIncrement(producto.clave, producto.allowHalf)} disabled={qty >= avail} className="w-11 h-11 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Historial de salidas */}
            {historialSalidas.length > 0 && (
              <div className="mt-6 mb-4">
                <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Historial de salidas de hoy
                </p>
                <div className="space-y-2">
                  {historialSalidas.map(s => {
                    const total = Object.values(s.productos).reduce((sum, v) => sum + v, 0);
                    const statusBadge = {
                      recibido:  { cls: 'bg-green-100 text-green-700',  label: 'Recibido' },
                      cancelado: { cls: 'bg-zinc-100 text-zinc-400',    label: 'Cancelado' },
                    }[s.status] ?? { cls: 'bg-amber-100 text-amber-700', label: 'En tránsito' };
                    return (
                      <div key={s.id} className={`p-3 rounded-xl border ${s.status === 'cancelado' ? 'bg-zinc-50 border-zinc-100 opacity-60' : 'bg-zinc-50 border-zinc-200'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-zinc-900">{s.destino_nombre}</span>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.cls}`}>
                              {statusBadge.label}
                            </span>
                            {s.status === 'en_transito' && cancelando !== s.id && (
                              <button
                                onClick={() => setCancelando(s.id)}
                                className="w-6 h-6 flex items-center justify-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                                title="Cancelar tránsito"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 mb-0.5">
                          {PRODUCTOS.filter(p => s.productos[p.clave]).map(p => (
                            <span key={p.clave} className="text-xs text-zinc-700">
                              {p.nombre}: <span className="font-semibold text-zinc-900">{fmtQty(s.productos[p.clave])}</span>
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-zinc-500">{fmtQty(total)} piezas · {formatTime(s.created_at)}</p>
                        {s.status === 'recibido' && s.confirmado_por_nombre && (
                          <p className="text-xs text-zinc-400 mt-0.5">Confirmado por {s.confirmado_por_nombre}</p>
                        )}
                        {cancelando === s.id && (
                          <div className="mt-2 flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2">
                            <span className="text-xs text-red-700 flex-1">¿Cancelar este tránsito?</span>
                            <button
                              onClick={() => handleCancelarTransito(s.id)}
                              className="px-3 h-7 bg-red-600 text-white text-xs font-medium rounded-lg"
                            >
                              Sí, cancelar
                            </button>
                            <button
                              onClick={() => setCancelando(null)}
                              className="px-3 h-7 border border-zinc-300 text-xs font-medium rounded-lg"
                            >
                              No
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-200 px-4 py-4 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-zinc-500">Total piezas</p>
                <p className="text-3xl font-bold text-orange-600">{fmtQty(totalPiezas())}</p>
              </div>
              {destino && <span className="px-3 py-1 bg-zinc-100 rounded-full text-sm text-zinc-700">{puntosVenta.find(p => p.id === destino)?.name}</span>}
            </div>
            <button
              onClick={handleSubmitSalida}
              disabled={submitting || totalPiezas() === 0 || !destino}
              className="w-full h-14 bg-orange-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-confirmar-movimiento"
            >
              {submitting ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Truck className="w-5 h-5" />Enviar en tránsito</>}
            </button>
          </div>
        </>
      )}

      {/* ===== ENTRADA ===== */}
      {tipo === 'entrada' && !transitoSeleccionado && (
        <div className="flex-1 overflow-auto px-4 py-4">
          {transitos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
              <PackageCheck className="w-16 h-16 mb-4" />
              <p className="text-lg font-medium">Sin envíos pendientes</p>
              <p className="text-sm mt-1">No hay tránsitos en camino a este punto</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide">Tránsitos pendientes de recibir</p>
              <div className="space-y-3">
                {transitos.map(t => {
                  const total = Object.values(t.productos).reduce((s, v) => s + v, 0);
                  return (
                    <button
                      key={t.id}
                      onClick={() => selectTransito(t)}
                      className="w-full p-4 bg-white border-2 border-zinc-200 rounded-xl text-left hover:border-blue-300 transition-all"
                      data-testid={`transito-${t.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-amber-500" />
                          <span className="font-bold text-zinc-900">{t.origen_nombre}</span>
                        </div>
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">En tránsito</span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 mb-1">
                        {PRODUCTOS.filter(p => t.productos[p.clave]).map(p => (
                          <span key={p.clave} className="text-xs text-zinc-700">
                            {p.nombre}: <span className="font-semibold text-zinc-900">{fmtQty(t.productos[p.clave])}</span>
                          </span>
                        ))}
                      </div>
                      <p className="text-sm text-zinc-500">{fmtQty(total)} piezas · {t.fecha_origen}</p>
                      <p className="text-xs text-zinc-400 mt-1">Enviado por {t.creado_por_nombre}</p>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Historial de entradas confirmadas */}
          <div className="mt-6 mb-4">
            <p className="text-xs text-zinc-500 font-medium mb-3 uppercase tracking-wide flex items-center gap-1">
              <Clock className="w-3 h-3" /> Entradas del turno
            </p>
            {historialEntradas.length === 0 ? (
              <p className="text-sm text-zinc-400 text-center py-4">Sin entradas confirmadas en este turno</p>
            ) : (
              <div className="space-y-2">
                {historialEntradas.map(e => {
                  const totalEnviado = Object.values(e.productos).reduce((sum, v) => sum + v, 0);
                  const totalRecibido = Object.values(e.productos_confirmados || {}).reduce((sum, v) => sum + v, 0);
                  return (
                    <div key={e.id} className="p-3 bg-zinc-50 rounded-xl border border-zinc-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-zinc-900">{e.origen_nombre}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400">{formatTime(e.confirmed_at)}</span>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Recibido</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {PRODUCTOS.filter(p => (e.productos_confirmados || e.productos)[p.clave]).map(p => {
                          const recibido = (e.productos_confirmados || {})[p.clave] || 0;
                          const enviado = e.productos[p.clave] || 0;
                          const diff = recibido - enviado;
                          return (
                            <span key={p.clave} className="text-xs text-zinc-600">
                              {p.nombre}: <span className="font-medium text-zinc-900">{fmtQty(recibido)}</span>
                              {diff < 0 && <span className="text-amber-600 ml-0.5">({fmtQty(enviado)} env.)</span>}
                            </span>
                          );
                        })}
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {fmtQty(totalRecibido)} piezas recibidas
                        {totalRecibido !== totalEnviado && <span className="text-amber-600"> · {fmtQty(totalEnviado)} enviadas</span>}
                      </p>
                      {e.confirmado_por_nombre && (
                        <p className="text-xs text-zinc-400 mt-0.5">Por {e.confirmado_por_nombre}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== CONFIRMAR ENTRADA ===== */}
      {tipo === 'entrada' && transitoSeleccionado && (
        <>
          <div className="px-4 pt-4 pb-2 flex items-center gap-2 bg-blue-50 border-b border-blue-100">
            <button onClick={() => { setTransitoSeleccionado(null); setRechazando(false); setMotivoRechazo(''); setError(''); }} className="text-sm text-blue-600 font-medium">← Volver</button>
            <span className="text-sm text-zinc-600">Confirmando entrada de <strong>{transitoSeleccionado.origen_nombre}</strong></span>
          </div>

          <div className="flex-1 overflow-auto px-4 py-2">
            <p className="text-xs text-zinc-500 mb-3">Ajusta las cantidades realmente recibidas</p>
            <div className="space-y-1">
              {PRODUCTOS.filter(p => transitoSeleccionado.productos[p.clave]).map(producto => {
                const enviado = transitoSeleccionado.productos[producto.clave] || 0;
                const conf = cantConfirmadas[producto.clave] || 0;
                return (
                  <div key={producto.clave} className="flex items-center justify-between py-3 border-b border-zinc-100">
                    <div>
                      <p className="text-base font-medium text-zinc-900">{producto.nombre}</p>
                      <p className="text-xs text-zinc-500">Enviado: {fmtQty(enviado)}{producto.allowHalf && <span className="ml-1 text-amber-600 font-medium">· ½</span>}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDecrementConf(producto.clave, producto.allowHalf)} disabled={conf === 0} className="w-11 h-11 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30">
                        <Minus className="w-5 h-5" />
                      </button>
                      <input
                        type="number"
                        inputMode={producto.allowHalf ? 'decimal' : 'numeric'}
                        min="0"
                        max={enviado}
                        step={producto.allowHalf ? '0.5' : '1'}
                        value={conf}
                        onChange={e => {
                          const v = Math.min(enviado, Math.max(0, parseQty(e.target.value, producto.allowHalf)));
                          setCantConfirmadas(prev => ({ ...prev, [producto.clave]: v }));
                        }}
                        className={`w-16 text-center text-2xl font-bold bg-transparent border-none outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${conf > 0 ? 'text-blue-600' : 'text-zinc-300'}`}
                      />
                      <button onClick={() => handleIncrementConf(producto.clave, producto.allowHalf)} disabled={conf >= enviado} className="w-11 h-11 flex items-center justify-center border border-zinc-300 rounded-lg disabled:opacity-30">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-zinc-200 px-4 py-4 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-zinc-500">Total confirmado</p>
                <p className="text-3xl font-bold text-blue-600">{fmtQty(totalConf())}</p>
              </div>
            </div>
            <button
              onClick={handleConfirmarEntrada}
              disabled={submitting || totalConf() === 0}
              className="w-full h-14 bg-blue-600 text-white rounded-xl text-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-confirmar-movimiento"
            >
              {submitting ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Check className="w-5 h-5" />Confirmar recepción</>}
            </button>
            {!rechazando ? (
              <button
                onClick={() => { setRechazando(true); setError(''); }}
                disabled={submitting}
                className="w-full h-12 mt-2 border border-red-300 text-red-600 rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="btn-rechazar-entrada"
              >
                <PackageX className="w-5 h-5" />No recibí esto: rechazar
              </button>
            ) : (
              <div className="mt-3 p-3 border border-red-200 bg-red-50 rounded-xl space-y-2">
                <p className="text-sm font-medium text-red-800">¿Por qué rechazas esta entrada?</p>
                <textarea
                  value={motivoRechazo}
                  onChange={e => setMotivoRechazo(e.target.value)}
                  rows={2}
                  placeholder="Ej. no llegó el pollo, llegó a otro punto, cantidades equivocadas..."
                  className="w-full p-2 text-sm border border-red-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 bg-white"
                  data-testid="input-motivo-rechazo"
                />
                <p className="text-xs text-red-700">No entra a tu inventario. Quedará en la bitácora y en tu corte.</p>
                <div className="flex gap-2">
                  <button onClick={() => { setRechazando(false); setMotivoRechazo(''); }} className="flex-1 h-11 border border-zinc-300 rounded-lg text-sm font-medium bg-white">
                    Cancelar
                  </button>
                  <button
                    onClick={handleRechazarEntrada}
                    disabled={submitting || motivoRechazo.trim().length < 3}
                    className="flex-1 h-11 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                    data-testid="btn-confirmar-rechazo"
                  >
                    Rechazar entrada
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
